'use client';

import { useContext, useEffect, useState } from 'react';

import servicesObject from '@/shared/config/services';
import { YmapClinicClient } from '@/shared/types/ymaps';
import { Daily, Tooltip } from '@/shared/ui';

import { ClinicsContext } from '..';

import ClinicsLoading from './ClinicsLoading';

type CategoryName = keyof typeof servicesObject;

export default function ClinicsList() {
  const { categoryName, ymapCompany, setClinicsCount, isLoading } = useContext(ClinicsContext);

  const [clinics, setClinics] = useState<YmapClinicClient['clinics'] | undefined>(
    ymapCompany?.clinics
  );

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  useEffect(() => {
    if (ymapCompany) {
      if (categoryName === 'Категория не выбрана') {
        setClinics(ymapCompany.clinics);
        setClinicsCount(ymapCompany.clinics.length);
      } else {
        const filteredClinics = ymapCompany.clinics?.filter((clinic) =>
          clinic.companyMetaData.categories.some((cat) => cat.name === categoryName)
        );
        setClinics(filteredClinics);
        setClinicsCount(filteredClinics?.length);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryName, ymapCompany]);

  if (isLoading || !ymapCompany?.clinics) return <ClinicsLoading />;

  return (
    <section className="grid grid-flow-row justify-center gap-6 pt-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
      {!!clinics?.length
        ? clinics.map((clinic) => {
            const isWorkingDaily = clinic.companyMetaData.hours.availabilities.some(
              (av) => av.Everyday
            );

            return (
              <section
                key={clinic.id}
                className="w-full max-w-[340px] rounded-md border-2 border-gray-600 p-4 shadow-lg shadow-gray-200 duration-300 hover:-translate-y-px hover:bg-slate-100 dark:shadow-gray-900 dark:hover:bg-[#141323]/20"
              >
                <h3 className="min-h-16 text-xl font-semibold">{clinic.name}</h3>
                <hr
                  className={`my-2 h-0.5 border-t-0 bg-neutral-400 opacity-100 dark:bg-neutral-100 dark:opacity-50`}
                />
                <div className="inline-flex min-h-24 w-full items-center justify-between py-4">
                  <figure>
                    {isWorkingDaily && (
                      <Tooltip alignment="right" text="Работаем без выходных">
                        <Daily />
                      </Tooltip>
                    )}
                  </figure>
                  <figure className="inline-flex">
                    {clinic.companyMetaData.categories.map((cat, i) => (
                      <Tooltip key={i} text={servicesObject[cat.name as CategoryName]?.text}>
                        {servicesObject[cat.name as CategoryName]?.icon}
                      </Tooltip>
                    ))}
                  </figure>
                </div>
                <article className="space-y-2">
                  <p>{clinic.companyMetaData.address}</p>
                  <p>{clinic.companyMetaData.hours.text}</p>
                  {isMobile ? (
                    <a
                      className="text-lg text-blue-600 underline sm:text-base dark:text-yellow-300"
                      href={`yandexmaps://${clinic.uri}`}
                    >
                      В Яндекс Картах
                    </a>
                  ) : (
                    <a
                      className="text-blue-600 underline dark:text-yellow-300"
                      target="_blank"
                      href={`https://yandex.ru/maps/?pt=${clinic.coordinates.coordinates.join(
                        ','
                      )}&z=18`}
                    >
                      В Яндекс Картах
                    </a>
                  )}
                  <div className="block w-full">
                    <span>Вебсайт:</span>&nbsp;
                    {clinic.companyMetaData.url ? (
                      <a
                        href={clinic.companyMetaData.url}
                        target="_blank"
                        className="text-blue-600 underline dark:text-yellow-300"
                      >
                        <p className="truncate text-lg sm:text-base">
                          {clinic.companyMetaData.url}
                        </p>
                      </a>
                    ) : (
                      <span>отсутствует</span>
                    )}
                  </div>
                  <div className="h-full space-y-2 rounded-md bg-slate-300 p-2 dark:bg-slate-600">
                    <p>Телефоны</p>
                    <ul>
                      {clinic.companyMetaData.phones.map((p) => (
                        <li key={p.formatted} className="hover:underline focus:underline">
                          <a href={`tel:${p.formatted}`}>{p.formatted}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </section>
            );
          })
        : !isLoading && (
            <section className="col-span-3 flex h-[20vw] items-center justify-center">
              <p className="text-4xl md:text-5xl">Ничего не найдено</p>
            </section>
          )}
    </section>
  );
}
